import {
  Box,
  Button,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import type { MultiValue } from 'chakra-react-select';
import { useState } from 'react';
import { useMutation, useQuery } from 'urql';

import type { ApiKeyEndpoint } from '@revelio/data-access';
import { FormField, Option, Select } from '@revelio/ui';

import { AlertDialog } from '../../../shared/components/alert-dialog';
import {
  ApiKeyManagementQuery,
  RegisterApiKeyMutation,
  RemoveApiKeyMutation,
} from '../userOperations';

type EndpointOption = {
  label: string;
  value: ApiKeyEndpoint;
};

type Props = {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
};

type PendingAction =
  | {
      type: 'delete';
      apiKeyId: string;
      endpoints: ApiKeyEndpoint[];
    }
  | {
      type: 'regenerate';
      apiKeyId: string;
      endpoints: ApiKeyEndpoint[];
    };

const formatReadableDate = (value?: string | null) => {
  if (!value) {
    return 'Unknown date';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown date';
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatFullDateTime = (value?: string | null) => {
  if (!value) {
    return 'Unknown date';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown date';
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

const sortApiKeysByCreatedAtDesc = <T extends { created_at?: string | null }>(
  apiKeys: T[]
) => {
  return [...apiKeys].sort((left, right) => {
    const leftTimestamp = left.created_at
      ? new Date(left.created_at).getTime()
      : 0;
    const rightTimestamp = right.created_at
      ? new Date(right.created_at).getTime()
      : 0;

    const normalizedLeftTimestamp = Number.isNaN(leftTimestamp)
      ? 0
      : leftTimestamp;
    const normalizedRightTimestamp = Number.isNaN(rightTimestamp)
      ? 0
      : rightTimestamp;

    return normalizedRightTimestamp - normalizedLeftTimestamp;
  });
};

const UserApiKeyManagement = ({ userId, isOpen, onClose }: Props) => {
  const toast = useToast();
  const {
    isOpen: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure();
  const {
    isOpen: isResultModalOpen,
    onOpen: onResultModalOpen,
    onClose: onResultModalClose,
  } = useDisclosure();
  const [
    { data: apiKeyManagementData, fetching: loadingApiKeys },
    refetchApiKeys,
  ] = useQuery({
    query: ApiKeyManagementQuery,
    variables: {
      input: {
        uuid: userId,
      },
    },
    requestPolicy: 'network-only',
    pause: !isOpen && !isCreateModalOpen,
  });
  const [{ fetching: creatingApiKey }, registerApiKey] = useMutation(
    RegisterApiKeyMutation
  );
  const [, removeApiKey] = useMutation(RemoveApiKeyMutation);
  const [selectedEndpoints, setSelectedEndpoints] = useState<ApiKeyEndpoint[]>(
    []
  );
  const [createError, setCreateError] = useState<string | undefined>();
  const [activeRemoveKeyId, setActiveRemoveKeyId] = useState<
    string | undefined
  >(undefined);
  const [activeRegenerateKeyId, setActiveRegenerateKeyId] = useState<
    string | undefined
  >(undefined);
  const [openActionMenuKeyId, setOpenActionMenuKeyId] = useState<
    string | undefined
  >(undefined);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );
  const [latestCreatedKey, setLatestCreatedKey] = useState<{
    key: string;
    keyId: string;
  } | null>(null);

  const endpointOptions: EndpointOption[] = (
    apiKeyManagementData?.availableApiKeyEndpoints ?? []
  ).map((endpoint) => ({
    label: endpoint,
    value: endpoint,
  }));

  const onSelectEndpoints = (options: MultiValue<EndpointOption>) => {
    const endpoints = options.map((option) => option.value);
    setSelectedEndpoints(endpoints);
    if (endpoints.length > 0) {
      setCreateError(undefined);
    }
  };

  const refreshApiKeys = () => {
    refetchApiKeys({ requestPolicy: 'network-only' });
  };

  const onCopyLatestKey = async () => {
    if (!latestCreatedKey?.key) {
      return;
    }

    try {
      await navigator.clipboard.writeText(latestCreatedKey.key);
      toast({
        title: 'API key copied',
        description: 'The API key has been copied to your clipboard.',
        status: 'success',
        duration: 2500,
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Could not copy API key',
        description: 'Please copy the key manually.',
        status: 'error',
        duration: 3000,
        position: 'top-right',
      });
    }
  };

  const onCreateApiKey = async () => {
    if (selectedEndpoints.length === 0) {
      setCreateError('Select at least one endpoint.');
      return;
    }

    const result = await registerApiKey({
      input: {
        uuid: userId,
        apis: selectedEndpoints,
      },
    });

    if (result.error) {
      toast({
        title: 'Error creating API key',
        description: 'Could not create API key for this user.',
        status: 'error',
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    const apiKey = result.data?.registerApiKey?.api_key;
    const apiKeyId = result.data?.registerApiKey?.api_key_id;

    if (apiKey && apiKeyId) {
      setLatestCreatedKey({
        key: apiKey,
        keyId: apiKeyId,
      });
      onCloseCreateModal();
      onResultModalOpen();
    } else {
      setLatestCreatedKey(null);
    }

    setSelectedEndpoints([]);
    setCreateError(undefined);
    refreshApiKeys();

    toast({
      title: 'API key created',
      description: 'A new API key was created for this user.',
      status: 'success',
      duration: 3000,
      position: 'top-right',
    });
  };

  const onOpenCreateModal = () => {
    setCreateError(undefined);
    setLatestCreatedKey(null);
    onCreateModalOpen();
  };

  const onCloseCreateModal = () => {
    setSelectedEndpoints([]);
    setCreateError(undefined);
    onCreateModalClose();
  };
  const onCloseResultModal = () => {
    setLatestCreatedKey(null);
    onResultModalClose();
  };

  const onRemoveApiKey = async (apiKeyId: string) => {
    setActiveRemoveKeyId(apiKeyId);

    const result = await removeApiKey({
      input: {
        uuid: userId,
        api_key_id: apiKeyId,
      },
    });

    setActiveRemoveKeyId(undefined);

    if (result.error || result.data?.removeApiKey !== true) {
      toast({
        title: 'Error removing API key',
        description: 'Could not remove API key for this user.',
        status: 'error',
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    refreshApiKeys();
    toast({
      title: 'API key removed',
      description: 'The API key was removed.',
      status: 'success',
      duration: 3000,
      position: 'top-right',
    });
  };

  const onRegenerateApiKey = async (
    apiKeyId: string,
    endpoints: ApiKeyEndpoint[]
  ) => {
    setActiveRegenerateKeyId(apiKeyId);

    const removeResult = await removeApiKey({
      input: {
        uuid: userId,
        api_key_id: apiKeyId,
      },
    });

    if (removeResult.error || removeResult.data?.removeApiKey !== true) {
      setActiveRegenerateKeyId(undefined);
      toast({
        title: 'Error regenerating API key',
        description: 'Could not remove existing key.',
        status: 'error',
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    const registerResult = await registerApiKey({
      input: {
        uuid: userId,
        apis: endpoints,
      },
    });

    setActiveRegenerateKeyId(undefined);

    if (registerResult.error) {
      toast({
        title: 'Error regenerating API key',
        description: 'Could not create replacement API key.',
        status: 'error',
        duration: 4000,
        position: 'top-right',
      });
      return;
    }

    const apiKey = registerResult.data?.registerApiKey?.api_key;
    const createdKeyId = registerResult.data?.registerApiKey?.api_key_id;

    if (apiKey && createdKeyId) {
      setLatestCreatedKey({
        key: apiKey,
        keyId: createdKeyId,
      });
      onResultModalOpen();
    }

    refreshApiKeys();
    toast({
      title: 'API key regenerated',
      description: 'The key was regenerated with the same endpoints.',
      status: 'success',
      duration: 3000,
      position: 'top-right',
    });
  };

  const onConfirmPendingAction = async () => {
    if (!pendingAction) {
      return;
    }

    if (pendingAction.type === 'delete') {
      await onRemoveApiKey(pendingAction.apiKeyId);
      setPendingAction(null);
      return;
    }

    await onRegenerateApiKey(pendingAction.apiKeyId, pendingAction.endpoints);
    setPendingAction(null);
  };

  const sortedApiKeys = sortApiKeysByCreatedAtDesc(
    apiKeyManagementData?.listApiKeys?.api_keys ?? []
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">Manage API Keys</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <Box>
                <Text fontWeight="semibold" mb="2">
                  Existing Keys
                </Text>
                {loadingApiKeys && <Text fontSize="sm">Loading Keys...</Text>}
                {!loadingApiKeys && sortedApiKeys.length === 0 && (
                  <Text fontSize="sm" color="gray.600">
                    No API Keys
                  </Text>
                )}
                <Stack spacing="2" mt="2">
                  {sortedApiKeys.map((apiKey) => (
                    <Option
                      key={apiKey.api_key_id}
                      cssVars={{ '--ui-option-padding': '10px 12px' }}
                      endSlot={
                        <Menu
                          placement="bottom-end"
                          offset={[0, 2]}
                          onOpen={() =>
                            setOpenActionMenuKeyId(apiKey.api_key_id)
                          }
                          onClose={() => setOpenActionMenuKeyId(undefined)}
                        >
                          <MenuButton
                            as={IconButton}
                            icon={<IconDotGrid1x3Vertical size={14} />}
                            aria-label="API key actions"
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            borderRadius="4px"
                            onClick={(event) => event.stopPropagation()}
                            opacity={
                              openActionMenuKeyId === apiKey.api_key_id
                                ? 1
                                : 0.8
                            }
                            transition="opacity 0.15s"
                          />
                          <MenuList minW={28}>
                            <MenuItem
                              onClick={(event) => {
                                event.stopPropagation();
                                setPendingAction({
                                  type: 'regenerate',
                                  apiKeyId: apiKey.api_key_id,
                                  endpoints: apiKey.endpoints,
                                });
                              }}
                            >
                              Regenerate
                            </MenuItem>
                            <MenuItem
                              color="red.500"
                              onClick={(event) => {
                                event.stopPropagation();
                                setPendingAction({
                                  type: 'delete',
                                  apiKeyId: apiKey.api_key_id,
                                  endpoints: apiKey.endpoints,
                                });
                              }}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      }
                    >
                      <Stack spacing="0.5">
                        <Text fontSize="sm" fontWeight="semibold">
                          {apiKey.api_key_id}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          Endpoints: {apiKey.endpoints.join(', ')}
                        </Text>
                        <Tooltip label={formatFullDateTime(apiKey.created_at)}>
                          <Text fontSize="xs" color="gray.600">
                            Created by: {apiKey.created_by_name} on{' '}
                            {formatReadableDate(apiKey.created_at)}
                          </Text>
                        </Tooltip>
                      </Stack>
                    </Option>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button colorScheme="navyBlue" variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button colorScheme="green" onClick={onOpenCreateModal}>
              Create API Key
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isCreateModalOpen} onClose={onCloseCreateModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">Create API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <FormField
                label="Enabled Endpoints"
                errors={createError ? [createError] : undefined}
              >
                <Select<EndpointOption, true>
                  isMulti
                  options={endpointOptions}
                  value={endpointOptions.filter((option) =>
                    selectedEndpoints.includes(option.value)
                  )}
                  onChange={onSelectEndpoints}
                  placeholder="Select one or more endpoints..."
                  closeMenuOnSelect={false}
                  noOptionsMessage={() => 'No endpoints'}
                />
              </FormField>
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              colorScheme="navyBlue"
              variant="ghost"
              onClick={onCloseCreateModal}
            >
              Close
            </Button>

            <Button
              colorScheme="green"
              onClick={onCreateApiKey}
              isLoading={creatingApiKey}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isResultModalOpen} onClose={onCloseResultModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">Generated Successfully</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              API key generated successfully. This will only be shown once.
              Please copy the key and distribute to the client securely.
            </Text>
            <Stack spacing="3">
              <Box
                p="3"
                borderWidth="1px"
                borderRadius="md"
                backgroundColor="gray.50"
              >
                <Text fontFamily="mono" fontSize="sm">
                  {latestCreatedKey?.key ?? ''}
                </Text>
                <HStack mt="2">
                  <Text fontSize="xs" color="gray.600">
                    Key ID: {latestCreatedKey?.keyId ?? ''}
                  </Text>
                </HStack>
              </Box>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={onCopyLatestKey}>
              Copy
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {pendingAction && (
        <AlertDialog
          isOpen={true}
          alertType="confirm"
          skipDefaultDisclosure={true}
          chakraAlertProps={{ isCentered: true }}
          headerProps={{ color: 'text.primary' }}
          onClose={() => setPendingAction(null)}
          headerText={
            pendingAction.type === 'delete'
              ? 'Delete API Key?'
              : 'Regenerate API key?'
          }
          bodyContent={
            <Text color="text.primary">
              {pendingAction.type === 'delete'
                ? 'This key will be removed and cannot be used again.'
                : 'This will invalidate the current key and create a new key with the same endpoints.'}
            </Text>
          }
          footerAcceptText={
            pendingAction.type === 'delete' ? 'Delete' : 'Regenerate'
          }
          footerAcceptIsDestructive={true}
          buttonLoading={
            (pendingAction.type === 'delete' &&
              activeRemoveKeyId === pendingAction.apiKeyId) ||
            (pendingAction.type === 'regenerate' &&
              activeRegenerateKeyId === pendingAction.apiKeyId)
          }
          onAccept={onConfirmPendingAction}
        />
      )}
    </>
  );
};

export default UserApiKeyManagement;
